import { useCallback, useEffect, useRef, useState } from 'react';
import { spellcheckApi, type SpellcheckIssue } from '@/api/spellcheck';
import { fnv1a } from '@/lib/hash';

type UseDebouncedSpellcheckParams = {
    enabled: boolean;
    text: string;
    reportId?: string;
    sectionId?: string;
    debounceMs?: number;
};

const DEFAULT_DEBOUNCE_MS = 1300;
const MAX_CACHE_ENTRIES = 300;

const spellcheckCache = new Map<string, SpellcheckIssue[]>();
const pendingSpellcheckRequests = new Map<string, Promise<SpellcheckIssue[]>>();

const cloneIssues = (issues: SpellcheckIssue[]) =>
    issues.map((issue) => ({
        ...issue,
        suggestions: [...issue.suggestions],
    }));

const hashText = (value: string) => `${fnv1a(value).toString(36)}:${value.length}`;

const getCacheKey = ({
    reportId,
    sectionId,
    text,
}: {
    reportId?: string;
    sectionId?: string;
    text: string;
}) => `${reportId ?? 'no-report'}:${sectionId ?? 'no-section'}:${hashText(text)}`;

const getCachedIssues = (cacheKey: string) => {
    const cached = spellcheckCache.get(cacheKey);

    return cached ? cloneIssues(cached) : null;
};

const setCachedIssues = (cacheKey: string, issues: SpellcheckIssue[]) => {
    if (spellcheckCache.size >= MAX_CACHE_ENTRIES) {
        const oldestKey = spellcheckCache.keys().next().value;

        if (oldestKey) {
            spellcheckCache.delete(oldestKey);
        }
    }

    spellcheckCache.set(cacheKey, cloneIssues(issues));
};

const requestSpellcheckIssues = (
    cacheKey: string,
    payload: Parameters<typeof spellcheckApi.check>[0],
) => {
    const pendingRequest = pendingSpellcheckRequests.get(cacheKey);

    if (pendingRequest) {
        return pendingRequest.then(cloneIssues);
    }

    const request = spellcheckApi
        .check(payload)
        .then((response) => {
            const nextIssues = response.data.issues;

            setCachedIssues(cacheKey, nextIssues);

            return nextIssues;
        })
        .finally(() => {
            pendingSpellcheckRequests.delete(cacheKey);
        });

    pendingSpellcheckRequests.set(cacheKey, request);

    return request.then(cloneIssues);
};

const shiftIssuesAfterCorrection = ({
    issues,
    correctedIssue,
    replacement,
}: {
    issues: SpellcheckIssue[];
    correctedIssue: SpellcheckIssue;
    replacement: string;
}) => {
    const delta = replacement.length - (correctedIssue.end - correctedIssue.start);

    return issues.flatMap((issue) => {
        const isCorrectedIssue =
            issue.start === correctedIssue.start &&
            issue.end === correctedIssue.end &&
            issue.text === correctedIssue.text;

        if (isCorrectedIssue) return [];
        if (issue.end <= correctedIssue.start) return [issue];

        if (issue.start >= correctedIssue.end) {
            return [
                {
                    ...issue,
                    start: issue.start + delta,
                    end: issue.end + delta,
                },
            ];
        }

        return [];
    });
};

export const useDebouncedSpellcheck = ({
    enabled,
    text,
    reportId,
    sectionId,
    debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseDebouncedSpellcheckParams) => {
    const [issues, setIssues] = useState<SpellcheckIssue[]>([]);
    const requestIdRef = useRef(0);

    const applyLocalCorrection = useCallback(
        (correctedIssue: SpellcheckIssue, replacement: string, nextText?: string) => {
            requestIdRef.current += 1;

            setIssues((currentIssues) => {
                const nextIssues = shiftIssuesAfterCorrection({
                    issues: currentIssues,
                    correctedIssue,
                    replacement,
                });
                const resolvedNextText =
                    nextText ??
                    `${text.slice(0, correctedIssue.start)}${replacement}${text.slice(
                        correctedIssue.end,
                    )}`;
                const nextCacheKey = getCacheKey({
                    reportId,
                    sectionId,
                    text: resolvedNextText,
                });

                setCachedIssues(nextCacheKey, nextIssues);

                return nextIssues;
            });
        },
        [reportId, sectionId, text],
    );

    useEffect(() => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;

        if (!enabled || !text.trim()) {
            setIssues([]);
            return;
        }

        const cacheKey = getCacheKey({ reportId, sectionId, text });
        const cachedIssues = getCachedIssues(cacheKey);

        if (cachedIssues) {
            setIssues(cachedIssues);
            return;
        }

        setIssues([]);

        let isActive = true;
        const timeoutId = window.setTimeout(() => {
            requestSpellcheckIssues(cacheKey, {
                report_id: reportId,
                section_id: sectionId,
                text,
            })
                .then((nextIssues) => {
                    if (!isActive || requestIdRef.current !== requestId) {
                        return;
                    }

                    setIssues(nextIssues);
                })
                .catch(() => {
                    // Spellcheck is opportunistic; failed checks should stay invisible.
                });
        }, debounceMs);

        return () => {
            isActive = false;
            window.clearTimeout(timeoutId);
        };
    }, [debounceMs, enabled, reportId, sectionId, text]);

    return { issues, applyLocalCorrection };
};
